import { useEffect, useRef, useState, type CSSProperties } from "react";

export function ReadinessDial({ readinessScore, status, fromScore, forecastScore, confidenceSpread }: { readinessScore: number; status: string; fromScore?: number; forecastScore?: number; confidenceSpread?: number }) {
  const startScore = fromScore ?? 0;
  const [displayed, setDisplayed] = useState(startScore);
  const [milestoneClass, setMilestoneClass] = useState("");
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    setDisplayed(startScore);
    setMilestoneClass("");
    const duration = 1400;
    const start = performance.now();
    const MILESTONES = [50, 75, 90];
    const triggered = new Set<number>();

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startScore + eased * (readinessScore - startScore));
      setDisplayed(current);

      for (const m of MILESTONES) {
        if (!triggered.has(m) && current >= m && startScore < m) {
          triggered.add(m);
          setMilestoneClass("dialMilestonePulse");
          setTimeout(() => setMilestoneClass(""), 700);
        }
      }

      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [readinessScore, startScore]);

  const showForecast = forecastScore !== undefined && forecastScore > readinessScore;
  const showCI = confidenceSpread !== undefined && confidenceSpread > 0;
  const ciLow = showCI ? Math.max(0, readinessScore - confidenceSpread!) * 3.6 : 0;
  const ciHigh = showCI ? Math.min(100, readinessScore + confidenceSpread!) * 3.6 : 0;

  return (
    <div
      className={`readinessDial ${milestoneClass}`}
      role="img"
      aria-label={`Readiness score: ${readinessScore}${showCI ? ` ±${confidenceSpread}` : ""}${showForecast ? `, potential: ${forecastScore}` : ""}`}
      style={{
        "--score": `${readinessScore * 3.6}deg`,
        ...(showForecast ? { "--forecast": `${forecastScore * 3.6}deg` } : {}),
        ...(showCI ? { "--ci-low": `${ciLow}deg`, "--ci-high": `${ciHigh}deg` } : {})
      } as CSSProperties}
    >
      {showForecast && <div className="forecastArcOverlay" aria-hidden="true" />}
      {showCI && <div className="confidenceArcOverlay" aria-hidden="true" />}
      <div>
        <strong>{displayed}</strong>
        <span>{status.replaceAll("_", " ")}</span>
        {showCI && <span className="confidenceLabel">±{confidenceSpread}</span>}
        {showForecast && <span className="forecastLabel">Potential: {forecastScore}</span>}
      </div>
    </div>
  );
}
